"use client";

import type { Dispatch, SetStateAction } from "react";
import { createContext, useContext } from "react";

import type { UserProfile } from "@/app/auth/_modules/types/user";

import { initialUserState } from ".";

interface IUserContext {
  user: UserProfile;
  refetchUser: (updater: (prev: UserProfile) => UserProfile) => void;
  isInitializing: boolean;
  /**
   * True when the last /users/profile fetch FAILED (threw, or there was no
   * session to fetch with) — as opposed to succeeding and telling us the user
   * has no record.
   *
   * ApprovalStatusGuard signs a user out and sends them to sign-up when it sees
   * no user id. Without this flag it cannot tell "the backend says you are not
   * registered" from "we could not ask the backend", and the second reading
   * hard-evicts a perfectly valid session during any outage.
   */
  profileLoadFailed: boolean;
  updateCurrentAgency?: (agency: { 
    id: string; 
    name?: string; 
    role?: string;
    description?: string;
    contactEmail?: string;
    isActive?: boolean;
    parentAgencyId?: string;
    createdAt?: string;
    updatedAt?: string;
    deletedAt?: string | null;
    permissions?: string[];
  }) => void;
  updateAgencyOnly?: (agency: any) => void;
}

export const UserContext = createContext<IUserContext>({
  user: initialUserState,
  refetchUser() {
    return;
  },
  isInitializing: true,
  profileLoadFailed: false,
  updateCurrentAgency: undefined,
  updateAgencyOnly: undefined,
});

export const useUserContext = () => {
  const context = useContext(UserContext);

  if (context === undefined)
    throw new Error("userContext was used outside of the userProvider");

  return context;
};
